import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  LinearProgress,
  CircularProgress,
  Chip,
  Button,
  IconButton,
  Card,
  CardContent,
  ListItemIcon,
  ListItemText,
  Fab,
  Menu,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SubscriptionModal from "../../Components/Reusable/SubscriptionModal";
import ConnectAccountsDialog from "./ConnectAccountsDialog";
import {
  TrendingUp,
  CheckCircle,
  Schedule,
  Groups,
  Facebook,
  LinkedIn,
  Instagram,
  Add as AddIcon,
  Refresh,
  AccessTime,
  Analytics,
  Rocket,
  PostAdd,
  Dashboard as DashboardIcon,
  Link,
  EmojiEvents,
  Lightbulb,
  LibraryBooks,
  VideoLibrary,
  Support,
  ArrowForward,
  ChevronLeft,
  ChevronRight,
  Close,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuthTokenPayload, getOwnMenuList } from "../../Utils";
import { fetchMonthlyPostCount, initiateTwitterOAuth } from "../../Action/api";
import FacebookAnalytics from "../../Components/FacebookAnalytics";
import InstagramAnalytics from "../../Components/InstagramAnalytics";
import LinkedInAnalytics from "../../Components/LinkedInAnalytics";

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// X (Twitter) has no icon in @mui/icons-material - render the glyph in the same slot as the other platform icons
const XIcon = ({ sx }) => (
  <Box
    component="span"
    sx={{
      fontWeight: 800,
      lineHeight: 1,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      userSelect: "none",
      ...sx,
    }}
  >
    𝕏
  </Box>
);

export const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [createMenuAnchor, setCreateMenuAnchor] = useState(null);
  // const [refreshing, setRefreshing] = useState(false);
  // const [activeStep, setActiveStep] = useState(0);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [connectingPlatform, setConnectingPlatform] = useState(null); // key of platform currently mid-OAuth-handoff (e.g. "x")
  const [connectErrorSnack, setConnectErrorSnack] = useState({ open: false, message: "" });
  const [pdfViewer, setPdfViewer] = useState({ open: false, url: "", platform: "", color: "" });
  const [pdfLoading, setPdfLoading] = useState(false);
  const [welcomeSnack, setWelcomeSnack] = useState(false);
  const [welcomePlan, setWelcomePlan] = useState("");
  const [dashboardView, setDashboardView] = useState("overview"); // "overview", "analytics", "instagram", or "linkedin"

  // Show welcome snackbar if arriving from successful payment
  useEffect(() => {
    if (location.state?.welcomePayment) {
      setWelcomePlan(location.state.planName || "your plan");
      setWelcomeSnack(true);
      // Clear state so refresh doesn't re-show it
      window.history.replaceState({}, document.title);
    }
  }, []);

  const userStatus = getAuthTokenPayload()?.userStatus;
  const watermarkLabel = "OruClick";

  // ── Monthly Post Usage (with month navigation) ─────────────────

  // Returns "Mar-2026" style string for an offset relative to today
  // offset 0 = current month, -1 = previous, +1 = next
  const getMonthYearByOffset = useCallback((offset) => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    return `${MONTH_NAMES[d.getMonth()]}-${d.getFullYear()}`;
  }, []);

  // month offset relative to current month (0 = now, negative = past)
  const [usageMonthOffset, setUsageMonthOffset] = useState(0);

  const [postUsageData, setPostUsageData] = useState({
    postCount: 0,
    allowPerMonthPost: null,
    monthYear: "",
    loading: true,
    error: null,
  });

  const fetchPostUsage = useCallback(async (offset = 0) => {
    const tokenPayload = getAuthTokenPayload();
    const clientId = tokenPayload?.clientId;
    if (!clientId) {
      setPostUsageData((prev) => ({ ...prev, loading: false, error: "Client ID not found" }));
      return;
    }
    const monthYear = getMonthYearByOffset(offset);
    // Get allowPerMonthPost from MENU-002 (Run Post) in this session's own menu permissions
    const menuList = getOwnMenuList();
    const runPostMenu = menuList.find(
      (m) => m.menuId === "MENU-002" || m.menuName === "Run Post"
    );
    // No cap data is known (the token no longer carries it) - null means "unknown", not "zero"
    const allowPerMonthPost = runPostMenu?.allowPerMonthPost ?? null;

    setPostUsageData((prev) => ({ ...prev, loading: true, monthYear }));
    const result = await fetchMonthlyPostCount(clientId, monthYear);
    if (result.success && result.data) {
      setPostUsageData({
        postCount: result.data.postCount ?? 0,
        allowPerMonthPost,
        monthYear,
        loading: false,
        error: null,
      });
    } else {
      setPostUsageData({
        postCount: 0,
        allowPerMonthPost,
        monthYear,
        loading: false,
        error: result.message || "Failed to load post count",
      });
    }
  }, [getMonthYearByOffset]);

  useEffect(() => {
    fetchPostUsage(0);
  }, [fetchPostUsage]);

  const handleUsageMonthChange = (delta) => {
    const newOffset = usageMonthOffset + delta;
    setUsageMonthOffset(newOffset);
    fetchPostUsage(newOffset);
  };
  // ───────────────────────────────────────────────────────────────

  // New user dashboard data (empty/getting started state)
  const [dashboardData, setDashboardData] = useState({
    isNewUser: true,
    connectedAccounts: {
      linkedin: 0,
      facebook: 0,
      instagram: 0,
      total: 0,
    },
    setupProgress: 0,
  });

  // Getting started steps
  const setupSteps = [
    {
      id: 1,
      title: "Connect Your First Platform",
      description: "Link your LinkedIn, Facebook, or Instagram account",
      icon: <Link sx={{ color: "#4f46e5" }} />,
      action: () => handleOpenConnectDialog(),
      completed: false,
      buttonText: "Connect",
    },
    {
      id: 2,
      title: "Create Your First Post",
      description: "Try our cross-platform posting feature",
      icon: <PostAdd sx={{ color: "#059669" }} />,
      action: () => navigate("/run-post"),
      completed: false,
      buttonText: "Create Post",
    },
    {
      id: 3,
      title: "Schedule Future Content",
      description: "Plan your content calendar",
      icon: <Schedule sx={{ color: "#7c3aed" }} />,
      action: () => navigate("/safe-post"),
      completed: false,
      buttonText: "Schedule",
    },
  ];

  // Sample upcoming features for new users
  const upcomingFeatures = [
    {
      title: "Advanced Analytics",
      description: "Deep insights into your social media performance",
      icon: <Analytics sx={{ color: "#0891b2" }} />,
      comingSoon: true,
    },
    {
      title: "Team Collaboration",
      description: "Work together with your team on content creation",
      icon: <Groups sx={{ color: "#e4405f" }} />,
      comingSoon: true,
    },
  ];

  // Quick start tips for new users
  const quickTips = [
    {
      title: "Best Posting Times",
      content: "LinkedIn: 8-10 AM, Facebook: 1-4 PM, Instagram: 6-9 PM",
      icon: <AccessTime />,
    },
    {
      title: "Content Ideas",
      content:
        "Share behind-the-scenes, industry insights, and customer stories",
      icon: <Lightbulb />,
    },
    {
      title: "Engagement Boost",
      content: "Use relevant hashtags and ask questions to increase engagement",
      icon: <TrendingUp />,
    },
  ];

  // Resources for new users
  const resources = [
    {
      title: "Getting Started Guide",
      description: "Complete walkthrough of all features",
      icon: <LibraryBooks sx={{ color: "#4f46e5" }} />,
      action: () => window.open("#", "_blank"),
    },
    {
      title: "Video Tutorials",
      description: "Watch step-by-step video guides",
      icon: <VideoLibrary sx={{ color: "#dc2626" }} />,
      action: () => window.open("#", "_blank"),
    },
    {
      title: "Live Support Chat",
      description: "Get help from our support team",
      icon: <Support sx={{ color: "#059669" }} />,
      action: () => window.open("#", "_blank"),
    },
  ];

  // Create menu handlers
  const handleCreateMenuOpen = (event) => {
    if (userStatus === "Interested") {
      setSubscriptionModalOpen(true);
      return;
    }
    setCreateMenuAnchor(event.currentTarget);
  };

  const handleCreateMenuClose = () => {
    setCreateMenuAnchor(null);
  };

  const handleOpenConnectDialog = () => {
    if (userStatus === "Interested") {
      setSubscriptionModalOpen(true);
      return;
    }
    setConnectDialogOpen(true);
  };

  const handleConnectPlatform = async (platformKey) => {
    // X (Twitter) uses a real OAuth handoff: fetch the authUrl from the backend, then redirect.
    if (platformKey === "x") {
      setConnectingPlatform("x");
      try {
        const result = await initiateTwitterOAuth();
        if (result.success && result.authUrl) {
          window.location.href = result.authUrl;
        } else {
          setConnectingPlatform(null);
          setConnectErrorSnack({
            open: true,
            message: result.message || "Failed to start Twitter connection. Please try again.",
          });
        }
      } catch (error) {
        setConnectingPlatform(null);
        setConnectErrorSnack({
          open: true,
          message: "Something went wrong while connecting to Twitter. Please try again.",
        });
      }
      return;
    }

    const providerMap = { facebook: "Facebook", instagram: "Instagram", linkedin: "LinkedIn" };
    const provider = providerMap[platformKey] || platformKey;
    setConnectDialogOpen(false);
    navigate("/settings", { state: { openAddAccount: true, provider } });
  };

  const handleCreateAction = (action) => {
    handleCreateMenuClose();
    switch (action) {
      case "crossPost":
        navigate("/run-post");
        break;
      case "schedule":
        navigate("/safe-post");
        break;
      case "connect":
        handleOpenConnectDialog();
        break;
      default:
        break;
    }
  };

  // Guard: show subscription modal instead of navigating for trial users
  const handleLockedAction = (callback) => {
    if (userStatus === "Interested") {
      setSubscriptionModalOpen(true);
      return;
    }
    callback();
  };

  return (
    <Box
      sx={{
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1, sm: 2 },
        bgcolor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Welcome Header */}
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 1.5, sm: 2 },
              mb: { xs: 2, md: 2.5 },
            }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: "#1e293b",
                  fontSize: { xs: "1.3rem", sm: "1.6rem", md: "1.75rem" },
                  lineHeight: 1.2,
                }}
              >
                📊 Social Media Command Center
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#64748b", mt: 0.3, fontSize: { xs: "0.82rem", sm: "0.9rem" } }}
              >
                Manage LinkedIn, Facebook &amp; Instagram from one place
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            </Stack>
          </Box>
        </Box>

        {/* Dashboard View Tabs */}
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          <Tabs
            value={dashboardView}
            onChange={(e, newValue) => setDashboardView(newValue)}
            sx={{
              bgcolor: "white",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            <Tab
              value="overview"
              label="Dashboard Overview"
              icon={<DashboardIcon />}
              iconPosition="start"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: { xs: "0.85rem", sm: "0.95rem" },
                minHeight: { xs: 56, sm: 64 },
              }}
            />
            <Tab
              value="analytics"
              label="Facebook Analytics"
              icon={<Analytics />}
              iconPosition="start"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: { xs: "0.85rem", sm: "0.95rem" },
                minHeight: { xs: 56, sm: 64 },
              }}
            />
            <Tab
              value="instagram"
              label="Instagram Analytics"
              icon={<Instagram />}
              iconPosition="start"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: { xs: "0.85rem", sm: "0.95rem" },
                minHeight: { xs: 56, sm: 64 },
              }}
            />
            <Tab
              value="linkedin"
              label="LinkedIn Analytics"
              icon={<LinkedIn />}
              iconPosition="start"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: { xs: "0.85rem", sm: "0.95rem" },
                minHeight: { xs: 56, sm: 64 },
              }}
            />
          </Tabs>
        </Box>

        {/* Conditional Rendering based on selected view */}
        {dashboardView === "analytics" ? (
          <FacebookAnalytics />
        ) : dashboardView === "instagram" ? (
          <InstagramAnalytics />
        ) : dashboardView === "linkedin" ? (
          <LinkedInAnalytics />
        ) : (
          <Box>
            {/* Getting Started Hero Section */}
            <Paper
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: { xs: 2, md: 4 },
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)",
              mb: { xs: 2, md: 3 },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                width: "100%",
                height: "100%",
                backgroundImage:
                  "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)",
                pointerEvents: "none",
              }}
            />

            <Grid container spacing={{ xs: 2, md: 4 }} alignItems="center">
              <Grid item xs={12} md={7}>
                <Box sx={{ position: "relative", zIndex: 1 }}>
                  <Typography
                    variant={isMobile ? "h5" : "h4"}
                    sx={{
                      fontWeight: 800,
                      mb: { xs: 1.5, md: 2 },
                      display: "flex",
                      alignItems: "center",
                      flexDirection: { xs: "column", sm: "row" },
                      textAlign: { xs: "center", sm: "left" },
                      gap: { xs: 1, sm: 2 },
                      fontSize: { xs: "1.1rem", sm: "1.4rem", md: "1.7rem" },
                    }}
                  >
                    <Rocket sx={{ fontSize: { xs: "1.6rem", md: "2rem" } }} />
                    Ready to Amplify Your Social Presence?
                  </Typography>
                  <Typography
                    variant={isMobile ? "body1" : "h6"}
                    sx={{
                      mb: { xs: 1.5, md: 2 },
                      fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                      lineHeight: 1.55,
                      opacity: 0.9,
                      fontWeight: 400,
                      textAlign: { xs: "center", sm: "left" },
                    }}
                  >
                    Connect your accounts and start posting to LinkedIn,
                    Facebook, Instagram, and X (Twitter) with just one click.
                    No more switching between platforms!
                  </Typography>

                  {/* Platform Icons Display */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: { xs: "center", sm: "flex-start" },
                      flexDirection: { xs: "column", sm: "row" },
                      gap: { xs: 1.5, sm: 2 },
                      mb: { xs: 2, md: 2.5 },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        opacity: 0.85,
                        fontSize: { xs: "0.82rem", sm: "0.88rem" },
                        textAlign: "center",
                      }}
                    >
                      Connect to:
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={{ xs: 1, sm: 2 }}
                      sx={{
                        flexWrap: { xs: "wrap", sm: "nowrap" },
                        justifyContent: "center",
                        gap: { xs: 1, sm: 2 },
                      }}
                    >
                      {[
                        { icon: LinkedIn, name: "LinkedIn" },
                        { icon: Facebook, name: "Facebook" },
                        { icon: Instagram, name: "Instagram" },
                        { icon: XIcon, name: "X (Twitter)" },
                      ].map((platform, index) => (
                        <Box
                          key={index}
                          sx={{
                            bgcolor: "rgba(255,255,255,0.2)",
                            borderRadius: 2,
                            p: { xs: 1, sm: 1.5 },
                            display: "flex",
                            alignItems: "center",
                            backdropFilter: "blur(10px)",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              bgcolor: "rgba(255,255,255,0.3)",
                              transform: "translateY(-2px)",
                            },
                          }}
                          onClick={handleOpenConnectDialog}
                          title={platform.name}
                        >
                          <platform.icon
                            sx={{
                              color: "white",
                              fontSize: { xs: "1.5rem", sm: "1.8rem" },
                            }}
                          />
                        </Box>
                      ))}
                    </Stack>
                  </Box>

                  <Grid container spacing={{ xs: 1, sm: 2 }}>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        size={isMobile ? "medium" : "large"}
                        startIcon={<Link />}
                        onClick={handleOpenConnectDialog}
                        sx={{
                          bgcolor: "rgba(255,255,255,0.2)",
                          backdropFilter: "blur(10px)",
                          border: "1px solid rgba(255,255,255,0.3)",
                          color: "white",
                          fontWeight: 700,
                          borderRadius: 3,
                          py: { xs: 1, sm: 1.5 },
                          width: "100%",
                          fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                          "&:hover": {
                            bgcolor: "rgba(255,255,255,0.3)",
                            transform: "translateY(-2px)",
                            boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        Connect Accounts
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    mt: { xs: 2, md: 0 },
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      fontWeight: 700,
                      opacity: 0.85,
                      letterSpacing: 1,
                      display: "block",
                      mb: 1.5,
                      fontSize: "0.72rem",
                    }}
                  >
                    📋 Step-by-step connection guides
                  </Typography>
                  <Stack spacing={1.25}>
                    {[
                      { name: "Facebook",  color: "#1877F2", pdf: "https://oruclicks-setup-docs.s3.ap-south-1.amazonaws.com/Oruclicks+-+SocialMedia+SetUp+(Facebook).pdf" },
                      { name: "Instagram", color: "#E1306C", pdf: "https://oruclicks-setup-docs.s3.ap-south-1.amazonaws.com/Oruclicks+-+SocialMedia+SetUp+(Instagram).pdf" },
                      { name: "LinkedIn",  color: "#0A66C2", pdf: "https://oruclicks-setup-docs.s3.ap-south-1.amazonaws.com/Oruclicks+-+SocialMedia+SetUp+(LinkedIn).pdf" },
                    ].map((p) => (
                      <Box
                        key={p.name}
                        onClick={() => handleLockedAction(() => { setPdfViewer({ open: true, url: p.pdf, platform: p.name, color: p.color }); setPdfLoading(true); })}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: "rgba(255,255,255,0.12)",
                          border: "1px solid rgba(255,255,255,0.2)",
                          backdropFilter: "blur(8px)",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: "rgba(255,255,255,0.22)",
                            transform: "translateX(3px)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 8, height: 8, borderRadius: "50%",
                            bgcolor: p.color, flexShrink: 0,
                            boxShadow: `0 0 6px ${p.color}`,
                          }}
                        />
                        <Typography fontWeight={700} fontSize="0.9rem" color="white" sx={{ flex: 1 }}>
                          {p.name}
                        </Typography>
                        <Typography fontSize="0.75rem" sx={{ opacity: 0.7, color: "white" }}>
                          View guide →
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.75,
                      bgcolor: "rgba(255,255,255,0.1)",
                      borderRadius: 2,
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255,255,255,0.2)",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                      <Typography variant="body2" sx={{ opacity: 0.85, fontSize: "0.8rem", fontWeight: 600 }}>
                        Setup Progress
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, fontSize: "0.8rem", fontWeight: 800 }}>
                        {dashboardData.setupProgress}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={dashboardData.setupProgress}
                      sx={{
                        height: 7,
                        borderRadius: 4,
                        bgcolor: "rgba(255,255,255,0.2)",
                        "& .MuiLinearProgress-bar": { bgcolor: "#10b981", borderRadius: 4 },
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Getting Started Steps */}
          <Grid item xs={12} md={8}>
            <Paper
              sx={{
                p: { xs: 1.5, sm: 2, md: 2.5 },
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                mb: { xs: 2, md: 3 },
              }}
            >
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{
                  fontWeight: 700,
                  mb: { xs: 1.5, md: 2 },
                  color: "#1e293b",
                  fontSize: { xs: "1.05rem", sm: "1.2rem", md: "1.35rem" },
                }}
              >
                🚀 Getting Started Checklist
              </Typography>

              <Stack spacing={{ xs: 1.25, md: 1.5 }}>
                {setupSteps.map((step, index) => (
                  <Card
                    key={step.id}
                    sx={{
                      border: step.completed
                        ? "2px solid #10b981"
                        : "2px solid #e2e8f0",
                      borderRadius: 3,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: { xs: "none", md: "translateY(-2px)" },
                        boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                        borderColor: step.completed ? "#10b981" : "#4f46e5",
                      },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: { xs: "flex-start", sm: "center" },
                          justifyContent: "space-between",
                          gap: { xs: 2, sm: 0 },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: 2, sm: 3 },
                            flex: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: { xs: 50, sm: 60 },
                              height: { xs: 50, sm: 60 },
                              borderRadius: "50%",
                              bgcolor: step.completed ? "#dcfce7" : "#f1f5f9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {step.completed ? (
                              <CheckCircle
                                sx={{
                                  color: "#10b981",
                                  fontSize: { xs: "1.5rem", sm: "2rem" },
                                }}
                              />
                            ) : (
                              React.cloneElement(step.icon, {
                                sx: { fontSize: { xs: "1.5rem", sm: "2rem" } },
                              })
                            )}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant={isMobile ? "body1" : "h6"}
                              sx={{
                                fontWeight: 700,
                                color: "#1e293b",
                                mb: { xs: 0.5, sm: 1 },
                                fontSize: {
                                  xs: "1rem",
                                  sm: "1.2rem",
                                  md: "1.25rem",
                                },
                              }}
                            >
                              {step.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#64748b",
                                fontSize: {
                                  xs: "0.8rem",
                                  sm: "0.85rem",
                                  md: "0.9rem",
                                },
                              }}
                            >
                              {step.description}
                            </Typography>
                          </Box>
                        </Box>
                        <Button
                          variant={step.completed ? "outlined" : "contained"}
                          onClick={() => handleLockedAction(step.action)}
                          startIcon={
                            step.completed ? <CheckCircle /> : <ArrowForward />
                          }
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            px: { xs: 2, sm: 3 },
                            bgcolor: step.completed ? "transparent" : "#4f46e5",
                            color: step.completed ? "#10b981" : "white",
                            borderColor: step.completed ? "#10b981" : "#4f46e5",
                            fontSize: { xs: "0.8rem", sm: "0.85rem" },
                            minWidth: { xs: "auto", sm: "120px" },
                            alignSelf: { xs: "flex-end", sm: "center" },
                            "&:hover": {
                              bgcolor: step.completed ? "#f0fdf4" : "#3730a3",
                            },
                          }}
                        >
                          {step.completed ? "Done" : step.buttonText}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Paper>

            {/* Quick Tips Section */}
            <Paper
              sx={{
                p: { xs: 1.5, sm: 2, md: 2.5 },
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              }}
            >
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{
                  fontWeight: 700,
                  mb: { xs: 1.5, md: 2 },
                  color: "#1e293b",
                  fontSize: { xs: "1.05rem", sm: "1.2rem", md: "1.35rem" },
                }}
              >
                💡 Pro Tips for Success
              </Typography>

              <Grid container spacing={{ xs: 2, md: 3 }}>
                {quickTips.map((tip, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <Box
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 2,
                        bgcolor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        height: "100%",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          bgcolor: "#f1f5f9",
                          transform: { xs: "none", md: "translateY(-2px)" },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: { xs: 1.5, sm: 2 },
                          mb: { xs: 1.5, sm: 2 },
                          flexDirection: { xs: "row", sm: "row" },
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 35, sm: 40 },
                            height: { xs: 35, sm: 40 },
                            borderRadius: "50%",
                            bgcolor: "#e0e7ff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {React.cloneElement(tip.icon, {
                            sx: {
                              color: "#4f46e5",
                              fontSize: { xs: "1.2rem", sm: "1.5rem" },
                            },
                          })}
                        </Box>
                        <Typography
                          variant={isMobile ? "body1" : "h6"}
                          sx={{
                            fontWeight: 700,
                            color: "#1e293b",
                            fontSize: {
                              xs: "0.95rem",
                              sm: "1.1rem",
                              md: "1.25rem",
                            },
                          }}
                        >
                          {tip.title}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#64748b",
                          fontSize: {
                            xs: "0.8rem",
                            sm: "0.85rem",
                            md: "0.9rem",
                          },
                          lineHeight: 1.5,
                        }}
                      >
                        {tip.content}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Sidebar with Resources and Features */}
          <Grid item xs={12} md={4}>

            {/* ── Monthly Post Usage Card ── */}
            {(() => {
              const hasKnownLimit = typeof postUsageData.allowPerMonthPost === "number";
              const usagePct =
                hasKnownLimit && postUsageData.allowPerMonthPost > 0
                  ? Math.round(
                      (postUsageData.postCount / postUsageData.allowPerMonthPost) * 100
                    )
                  : 0;
              const remaining = hasKnownLimit
                ? postUsageData.allowPerMonthPost - postUsageData.postCount
                : null;
              const usageColor =
                usagePct >= 90 ? "#ef4444" : usagePct >= 70 ? "#f59e0b" : "#10b981";
              const usageStatusBg =
                usagePct >= 90 ? "#fef2f2" : usagePct >= 70 ? "#fffbeb" : "#f0fdf4";
              const usageStatusBorder =
                usagePct >= 90 ? "#fecaca" : usagePct >= 70 ? "#fde68a" : "#bbf7d0";
              const usageStatusText =
                usagePct >= 90 ? "#dc2626" : usagePct >= 70 ? "#92400e" : "#065f46";
              const isCurrentMonth = usageMonthOffset === 0;
              const isPastMonth = usageMonthOffset < 0;
              const isFutureMonth = usageMonthOffset > 0;

              const usageStatusMsg = !hasKnownLimit
                ? `📊 ${postUsageData.postCount} post${postUsageData.postCount !== 1 ? "s" : ""} ${isPastMonth ? "were made" : "made"} this month`
                : isPastMonth
                ? usagePct >= 100
                  ? "📋 All posts were used this month"
                  : usagePct >= 70
                  ? `📋 ${Math.max(remaining, 0)} post${Math.max(remaining, 0) !== 1 ? "s" : ""} were unused`
                  : `📋 ${Math.max(remaining, 0)} post${Math.max(remaining, 0) !== 1 ? "s" : ""} went unused this month`
                : usagePct >= 100
                ? "⚠️ Monthly post limit reached!"
                : usagePct >= 90
                ? `⚡ Almost at limit — ${Math.max(remaining, 0)} post${
                    Math.max(remaining, 0) !== 1 ? "s" : ""
                  } remaining`
                : usagePct >= 70
                ? `📢 ${remaining} post${remaining !== 1 ? "s" : ""} left this month`
                : `✅ ${remaining} post${remaining !== 1 ? "s" : ""} available${isFutureMonth ? " to plan" : " this month"}`;

              return (
                <Paper
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    mb: { xs: 2, md: 3 },
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  {/* Card header */}
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: "1rem", md: "1.1rem" },
                          color: "#1e293b",
                          lineHeight: 1.2,
                        }}
                      >
                        📊 Monthly Post Usage
                      </Typography>
                      <Tooltip title="Refresh">
                        <IconButton
                          size="small"
                          onClick={() => fetchPostUsage(usageMonthOffset)}
                          disabled={postUsageData.loading}
                          sx={{ color: "#94a3b8" }}
                        >
                          <Refresh sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Month navigator row */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        bgcolor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        borderRadius: 2,
                        px: 1,
                        py: 0.5,
                      }}
                    >
                      <Tooltip title="Previous month">
                        <IconButton
                          size="small"
                          onClick={() => handleUsageMonthChange(-1)}
                          disabled={postUsageData.loading}
                          sx={{
                            color: "#475569",
                            "&:hover": { bgcolor: "#e2e8f0" },
                            p: 0.5,
                          }}
                        >
                          <ChevronLeft />
                        </IconButton>
                      </Tooltip>

                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#1e293b", lineHeight: 1.2 }}
                        >
                          {postUsageData.monthYear || getMonthYearByOffset(usageMonthOffset)}
                        </Typography>
                        {isCurrentMonth && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#4f46e5",
                              fontWeight: 600,
                              fontSize: "0.68rem",
                              letterSpacing: 0.3,
                            }}
                          >
                            Current month
                          </Typography>
                        )}
                        {isFutureMonth && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#0891b2",
                              fontWeight: 600,
                              fontSize: "0.68rem",
                              letterSpacing: 0.3,
                            }}
                          >
                            Future month
                          </Typography>
                        )}
                      </Box>

                      <Tooltip title={isFutureMonth ? "Next month" : isCurrentMonth ? "Next month" : "Next month"}>
                        <IconButton
                          size="small"
                          onClick={() => handleUsageMonthChange(+1)}
                          disabled={postUsageData.loading}
                          sx={{
                            color: "#475569",
                            "&:hover": { bgcolor: "#e2e8f0" },
                            p: 0.5,
                          }}
                        >
                          <ChevronRight />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box sx={{ position: "relative" }}>
                    {/* Subtle overlay while loading — keeps content visible */}
                    {postUsageData.loading && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          bgcolor: "rgba(255,255,255,0.6)",
                          zIndex: 2,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress size={28} sx={{ color: "#4f46e5" }} />
                      </Box>
                    )}

                    {/* Always-visible content */}
                    <Box sx={{ opacity: postUsageData.loading ? 0.45 : 1, transition: "opacity 0.2s" }}>
                      {/* Circular gauge */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mb: 2.5,
                          mt: 0.5,
                        }}
                      >
                        <Box sx={{ position: "relative", display: "inline-flex" }}>
                          {/* Background track */}
                          <CircularProgress
                            variant="determinate"
                            value={100}
                            size={130}
                            thickness={5}
                            sx={{
                              color: "#e2e8f0",
                              position: "absolute",
                              top: 0,
                              left: 0,
                            }}
                          />
                          {/* Filled arc */}
                          <CircularProgress
                            variant="determinate"
                            value={Math.min(usagePct, 100)}
                            size={130}
                            thickness={5}
                            sx={{ color: usageColor }}
                          />
                          {/* Centre label */}
                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 800,
                                fontSize: "1.9rem",
                                color: usageColor,
                                lineHeight: 1,
                              }}
                            >
                              {postUsageData.postCount}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#94a3b8", fontWeight: 600, mt: 0.3 }}
                            >
                              used
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Allowed / Remaining stat boxes */}
                      <Grid container spacing={1} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                          <Box
                            sx={{
                              textAlign: "center",
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: "#f8fafc",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 800,
                                fontSize: "1.4rem",
                                color: "#1e293b",
                                lineHeight: 1,
                              }}
                            >
                              {typeof postUsageData.allowPerMonthPost === "number" ? postUsageData.allowPerMonthPost : "—"}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#64748b", fontWeight: 500 }}
                            >
                              Allowed / mo
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box
                            sx={{
                              textAlign: "center",
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: !hasKnownLimit
                                ? "#f8fafc"
                                : isPastMonth
                                ? remaining <= 0 ? "#fef2f2" : "#f8fafc"
                                : remaining <= 0 ? "#fef2f2" : "#f0fdf4",
                              border: `1px solid ${
                                !hasKnownLimit
                                  ? "#e2e8f0"
                                  : isPastMonth
                                  ? remaining <= 0 ? "#fecaca" : "#e2e8f0"
                                  : remaining <= 0 ? "#fecaca" : "#bbf7d0"
                              }`,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 800,
                                fontSize: "1.4rem",
                                color: !hasKnownLimit
                                  ? "#94a3b8"
                                  : isPastMonth
                                  ? remaining <= 0 ? "#ef4444" : "#94a3b8"
                                  : remaining <= 0 ? "#ef4444" : "#10b981",
                                lineHeight: 1,
                              }}
                            >
                              {hasKnownLimit ? Math.max(remaining, 0) : "—"}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#64748b", fontWeight: 500 }}
                            >
                              {isPastMonth ? "Unused" : "Remaining"}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Linear progress with % label */}
                      <Box sx={{ mb: 1.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "#64748b", fontWeight: 600 }}
                          >
                            Usage
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: usageColor, fontWeight: 700 }}
                          >
                            {Math.min(usagePct, 100)}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(usagePct, 100)}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: "#e2e8f0",
                            "& .MuiLinearProgress-bar": {
                              bgcolor: usageColor,
                              borderRadius: 4,
                            },
                          }}
                        />
                      </Box>

                      {/* Status message chip */}
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: isPastMonth ? "#f8fafc" : usageStatusBg,
                          border: `1px solid ${isPastMonth ? "#e2e8f0" : usageStatusBorder}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: isPastMonth ? "#64748b" : usageStatusText,
                            fontWeight: 600,
                            display: "block",
                            textAlign: "center",
                            fontSize: "0.78rem",
                          }}
                        >
                          {usageStatusMsg}
                        </Typography>
                      </Box>
                    </Box>{/* end always-visible content */}
                  </Box>{/* end position:relative wrapper */}
                </Paper>
              );
            })()}
            {/* ─────────────────────────────────────────────────── */}

            {/* Resources */}
            {false && <Paper
              sx={{
                p: { xs: 1.5, sm: 2, md: 2.5 },
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                mb: { xs: 2, md: 3 },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: { xs: 1.5, md: 2 },
                  fontSize: { xs: "1rem", sm: "1.1rem", md: "1.15rem" },
                }}
              >
                📚 Learning Resources
              </Typography>

              <Stack spacing={{ xs: 1.5, md: 2 }}>
                {resources.map((resource, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: { xs: 1.25, sm: 1.75 },
                      borderRadius: 2,
                      border: "1px solid #e2e8f0",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: "#f8fafc",
                        borderColor: "#4f46e5",
                        transform: { xs: "none", md: "translateY(-1px)" },
                      },
                    }}
                    onClick={resource.action}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: 1.5, sm: 2 },
                        mb: { xs: 0.5, sm: 1 },
                      }}
                    >
                      {React.cloneElement(resource.icon, {
                        sx: { fontSize: { xs: "1.2rem", sm: "1.5rem" } },
                      })}
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: "0.9rem", sm: "1rem" },
                        }}
                      >
                        {resource.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#64748b",
                        fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        lineHeight: 1.4,
                      }}
                    >
                      {resource.description}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>}

            {/* Coming Soon Features */}
            <Paper
              sx={{
                p: { xs: 1.5, sm: 2, md: 2.5 },
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: { xs: 1.5, md: 2 },
                  fontSize: { xs: "1rem", sm: "1.1rem", md: "1.15rem" },
                }}
              >
                🔮 Coming Soon
              </Typography>

              <Stack spacing={{ xs: 1.5, md: 2 }}>
                {upcomingFeatures.map((feature, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: { xs: 1.25, sm: 1.75 },
                      borderRadius: 2,
                      border: "1px dashed #d1d5db",
                      bgcolor: "#f9fafb",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: 1, sm: 2 },
                        mb: { xs: 0.5, sm: 1 },
                        flexWrap: "wrap",
                      }}
                    >
                      {React.cloneElement(feature.icon, {
                        sx: { fontSize: { xs: "1.2rem", sm: "1.5rem" } },
                      })}
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          fontSize: { xs: "0.9rem", sm: "1rem" },
                          flex: 1,
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Chip
                        label="Soon"
                        size="small"
                        sx={{
                          bgcolor: "#fef3c7",
                          color: "#92400e",
                          fontSize: { xs: "0.6rem", sm: "0.7rem" },
                          fontWeight: 600,
                          height: { xs: 20, sm: 24 },
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6b7280",
                        fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                        lineHeight: 1.4,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
          </Box>
        )}

        {/* Floating Action Button */}
        <Fab
          color="primary"
          sx={{
            position: "fixed",
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            bgcolor: "#4f46e5",
            width: { xs: 56, sm: 64 },
            height: { xs: 56, sm: 64 },
            "&:hover": { bgcolor: "#3730a3", transform: "scale(1.1)" },
            transition: "all 0.3s ease",
            zIndex: 1000,
          }}
          onClick={handleCreateMenuOpen}
        >
          <AddIcon sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }} />
        </Fab>

        <SubscriptionModal
          open={subscriptionModalOpen}
          onClose={() => setSubscriptionModalOpen(false)}
        />

        <ConnectAccountsDialog
          open={connectDialogOpen}
          onClose={() => {
            setConnectDialogOpen(false);
            setConnectingPlatform(null);
          }}
          onConnect={handleConnectPlatform}
          connectingPlatform={connectingPlatform}
        />

        {/* Per-platform PDF guide viewer */}
        <Dialog
          open={pdfViewer.open}
          onClose={() => setPdfViewer((v) => ({ ...v, open: false }))}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, height: "90vh", display: "flex", flexDirection: "column" } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #e2e8f0",
              pb: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 12, height: 12, borderRadius: "50%",
                  bgcolor: pdfViewer.color,
                  boxShadow: `0 0 8px ${pdfViewer.color}`,
                }}
              />
              <Typography fontWeight={800} fontSize="1rem">
                {pdfViewer.platform} — Step-by-step Connection Guide
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setPdfViewer((v) => ({ ...v, open: false }))}>
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 0, flex: 1, overflow: "hidden" }}>
            <Box sx={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}>
              {pdfLoading && (
                <Box sx={{ position: "absolute", inset: 0, zIndex: 3, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: "rgba(255,255,255,0.85)", gap: 1.5 }}>
                  <CircularProgress size={40} sx={{ color: pdfViewer.color || "#4B5AB6" }} />
                  <Typography fontSize="0.82rem" fontWeight={600} color="#64748b">Loading guide…</Typography>
                </Box>
              )}
              <iframe
                key={pdfViewer.url}
                src={`${pdfViewer.url}#toolbar=0&navpanes=0&scrollbar=0`}
                title={`${pdfViewer.platform} Setup Guide`}
                style={{ border: "none", display: "block", position: "absolute", top: "-20px", left: 0, width: "100%", height: "calc(100% + 32px)" }}
                onLoad={() => setPdfLoading(false)}
              />
              {/* Watermark overlay */}
              <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none", overflow: "hidden", zIndex: 1, display: "grid", gridTemplateColumns: "repeat(3, 1fr)" }}>
                {Array.from({ length: 36 }).map((_, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "rgba(75,90,182,0.13)", transform: "rotate(-35deg)", userSelect: "none", whiteSpace: "nowrap", letterSpacing: "0.04em" }}>
                      {watermarkLabel}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setPdfViewer((v) => ({ ...v, open: false }))}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setPdfViewer((v) => ({ ...v, open: false }));
                handleOpenConnectDialog();
              }}
              sx={{
                bgcolor: pdfViewer.color || "#4f46e5",
                "&:hover": { bgcolor: pdfViewer.color || "#4f46e5", opacity: 0.85 },
                borderRadius: 2,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Connect {pdfViewer.platform} Now →
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Menu */}
        <Menu
          anchorEl={createMenuAnchor}
          open={Boolean(createMenuAnchor)}
          onClose={handleCreateMenuClose}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
              minWidth: { xs: 200, sm: 250 },
            },
          }}
        >
          <MenuItem
            onClick={() => handleCreateAction("connect")}
            sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 2, sm: 3 } }}
          >
            <ListItemIcon>
              <Link
                sx={{
                  color: "#4f46e5",
                  fontSize: { xs: "1.2rem", sm: "1.5rem" },
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Connect Accounts"
              secondary="Link your social media accounts"
              primaryTypographyProps={{
                fontSize: { xs: "0.85rem", sm: "0.9rem" },
              }}
              secondaryTypographyProps={{
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
              }}
            />
          </MenuItem>
          <MenuItem
            onClick={() => handleCreateAction("crossPost")}
            sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 2, sm: 3 } }}
          >
            <ListItemIcon>
              <PostAdd
                sx={{
                  color: "#059669",
                  fontSize: { xs: "1.2rem", sm: "1.5rem" },
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Create Post"
              secondary="Start posting to all platforms"
              primaryTypographyProps={{
                fontSize: { xs: "0.85rem", sm: "0.9rem" },
              }}
              secondaryTypographyProps={{
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
              }}
            />
          </MenuItem>
          <MenuItem
            onClick={() => handleCreateAction("schedule")}
            sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 2, sm: 3 } }}
          >
            <ListItemIcon>
              <Schedule
                sx={{
                  color: "#7c3aed",
                  fontSize: { xs: "1.2rem", sm: "1.5rem" },
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Schedule Content"
              secondary="Plan your content calendar"
              primaryTypographyProps={{
                fontSize: { xs: "0.85rem", sm: "0.9rem" },
              }}
              secondaryTypographyProps={{
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
              }}
            />
          </MenuItem>
        </Menu>
      </Container>

      {/* ── Post-Payment Welcome Snackbar ── */}
      <Snackbar
        open={welcomeSnack}
        autoHideDuration={8000}
        onClose={() => setWelcomeSnack(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ top: { xs: 72, sm: 80 } }}
      >
        <Alert
          onClose={() => setWelcomeSnack(false)}
          severity="success"
          variant="filled"
          icon={<EmojiEvents sx={{ fontSize: 26 }} />}
          sx={{
            width: "100%",
            maxWidth: 480,
            borderRadius: 3,
            py: 1.5,
            px: 2.5,
            background: "linear-gradient(135deg,#059669 0%,#10b981 100%)",
            boxShadow: "0 8px 32px rgba(5,150,105,0.35)",
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <Typography fontWeight={700} fontSize={15} color="#fff" lineHeight={1.3}>
            🎉 Welcome aboard! Your <strong>{welcomePlan}</strong> plan is now active.
          </Typography>
          <Typography fontSize={12.5} color="rgba(255,255,255,0.85)" mt={0.4}>
            You're all set — start posting, scheduling, and growing your audience.
          </Typography>
        </Alert>
      </Snackbar>

      {/* ── Connect-account error Snackbar (e.g. Twitter OAuth handoff failure) ── */}
      <Snackbar
        open={connectErrorSnack.open}
        autoHideDuration={6000}
        onClose={() => setConnectErrorSnack({ open: false, message: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ top: { xs: 72, sm: 80 } }}
      >
        <Alert
          onClose={() => setConnectErrorSnack({ open: false, message: "" })}
          severity="error"
          variant="filled"
          sx={{ width: "100%", maxWidth: 480, borderRadius: 3 }}
        >
          {connectErrorSnack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
